/**
 * Voting System Utilities
 * 
 * Shared utilities for the voting/clapping system to maintain DRY principles
 */

import { createHash } from 'crypto';
import { VOTING_CONSTANTS } from '@/types/voting';

/**
 * Hash IP address using SHA-256 for privacy
 * Prevents storing raw IPs while maintaining uniqueness
 * 
 * @param ip - Client IP address
 * @returns Hashed IP address
 */
export async function hashIP(ip: string): Promise<string> {
  const salt = process.env.IP_SALT || 'descout-votes-default-salt';
  
  // Check if salt is configured (log warning in development only)
  if (!process.env.IP_SALT && process.env.NODE_ENV === 'development') {
    console.warn('⚠️ IP_SALT not configured, using default salt. Set IP_SALT environment variable for production.');
  }
  
  // Use Node.js crypto (works in all Node.js environments including Vercel)
  const hash = createHash('sha256');
  hash.update(ip + salt);
  return hash.digest('hex');
}

/**
 * Calculate percentages for sentiment voting
 * 
 * @param bullishVotes - Number of bullish votes
 * @param bearishVotes - Number of bearish votes
 * @returns Object with bullish and bearish percentages
 */
export function calculateSentimentPercentages(
  bullishVotes: number,
  bearishVotes: number
): { bullishPercentage: number; bearishPercentage: number; totalVotes: number } {
  const totalVotes = bullishVotes + bearishVotes;
  
  if (totalVotes === 0) {
    return {
      bullishPercentage: 0,
      bearishPercentage: 0,
      totalVotes: 0,
    };
  }

  return {
    bullishPercentage: (bullishVotes / totalVotes) * 100,
    bearishPercentage: (bearishVotes / totalVotes) * 100,
    totalVotes,
  };
}

/**
 * Format vote count for display (e.g., 1500 -> "1.5K")
 * 
 * @param count - Vote count number
 * @returns Formatted string
 */
export function formatVoteCount(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return count.toString();
}

/**
 * Format percentage for display (e.g., 82.5 -> "83%")
 * 
 * @param percentage - Percentage number (0-100)
 * @returns Formatted string with % symbol
 */
export function formatPercentage(percentage: number): string {
  return `${Math.round(percentage)}%`;
}

/**
 * Extract client IP from request headers
 * Supports various hosting platforms (Vercel, Cloudflare, etc.)
 * 
 * @param request - Next.js Request object
 * @returns Client IP address or fallback
 */
export function getClientIP(request: Request): string {
  const headers = request.headers;
  
  // Try various headers in order of reliability
  
  // 1. x-forwarded-for (Vercel's standard header)
  // Format: "client-ip, proxy1-ip, proxy2-ip"
  const forwardedFor = headers.get('x-forwarded-for');
  if (forwardedFor) {
    const ips = forwardedFor.split(',').map(ip => ip.trim());
    // Get the first (original client) IP
    const clientIP = ips[0];
    if (clientIP && isValidIP(clientIP)) {
      return clientIP;
    }
  }
  
  // 2. x-real-ip (Alternative header used by some proxies)
  const realIP = headers.get('x-real-ip');
  if (realIP && isValidIP(realIP)) {
    return realIP;
  }
  
  // 3. cf-connecting-ip (Cloudflare specific)
  const cfConnectingIP = headers.get('cf-connecting-ip');
  if (cfConnectingIP && isValidIP(cfConnectingIP)) {
    return cfConnectingIP;
  }
  
  // 4. x-client-ip (Some CDNs)
  const xClientIP = headers.get('x-client-ip');
  if (xClientIP && isValidIP(xClientIP)) {
    return xClientIP;
  }
  
  // Fallback (should not happen in production)
  console.warn('[VOTE WARN] Unable to determine client IP from any header');
  return '0.0.0.0';
}

/**
 * Validate IP address format (IPv4 or IPv6)
 * 
 * @param ip - IP address string
 * @returns True if valid IP format
 */
function isValidIP(ip: string): boolean {
  if (!ip || ip === '0.0.0.0' || ip === '::') {
    return false;
  }
  
  // IPv4 validation (simple regex)
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4Regex.test(ip)) {
    const parts = ip.split('.');
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  }
  
  // IPv6 validation (basic check for colons)
  const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/;
  if (ipv6Regex.test(ip)) {
    return true;
  }
  
  return false;
}

/**
 * Validate clap count for article clapping
 * 
 * @param claps - Number of claps to add
 * @param currentUserClaps - User's current clap count
 * @returns Object with validation result and adjusted count
 */
export function validateClapCount(
  claps: number,
  currentUserClaps: number
): { valid: boolean; actualClaps: number; error?: string } {
  // Check minimum
  if (claps < VOTING_CONSTANTS.MIN_CLAPS) {
    return {
      valid: false,
      actualClaps: 0,
      error: `Minimum ${VOTING_CONSTANTS.MIN_CLAPS} clap required`,
    };
  }

  // Check per-action maximum
  if (claps > VOTING_CONSTANTS.MAX_CLAPS_PER_ACTION) {
    return {
      valid: false,
      actualClaps: 0,
      error: `Maximum ${VOTING_CONSTANTS.MAX_CLAPS_PER_ACTION} claps per action`,
    };
  }

  // Check total maximum
  if (currentUserClaps >= VOTING_CONSTANTS.MAX_CLAPS_PER_IP) {
    return {
      valid: false,
      actualClaps: 0,
      error: `Maximum ${VOTING_CONSTANTS.MAX_CLAPS_PER_IP} claps per article reached`,
    };
  }

  // Adjust if would exceed total maximum
  const actualClaps = Math.min(
    claps,
    VOTING_CONSTANTS.MAX_CLAPS_PER_IP - currentUserClaps
  );

  return {
    valid: true,
    actualClaps,
  };
}

/**
 * Get cooldown time remaining in seconds
 * 
 * @param lastVoteTime - Timestamp of last vote
 * @returns Seconds remaining in cooldown period
 */
export function getCooldownSeconds(lastVoteTime: Date | null): number {
  if (!lastVoteTime) return 0;

  const now = Date.now();
  const lastVote = lastVoteTime.getTime();
  const elapsed = now - lastVote;
  const remaining = VOTING_CONSTANTS.SENTIMENT_COOLDOWN - elapsed;

  return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
}


